import React from "react";
import { ToggleButton } from "primereact/togglebutton";
import { useSetting, useSettingsActions } from "./SettingsStore.tsx";
import { MAX_ROSTER, countOutOfRange, parseRoster } from "../ClassSet.ts";
import { levelApplies, levelOf, ladderForSettings } from "../FactLevels.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

/**
 * The class-set control (ADR 0011): a roster typed one name per line, and a
 * choice between the same worksheet for everyone or a different draw each.
 *
 * The roster is held by the parent in component state and passed in — it is
 * never persisted or encoded anywhere, by design. Only the mode is a setting.
 */
export function ClassRoster({
    roster,
    onRosterChange,
}: {
    roster: string;
    onRosterChange: (value: string) => void;
}) {
    const { getSettings, setSettings } = useSettingsActions();
    // The slice is what this control needs of the settings, and nothing more (ADR 0081): the
    // mode, and - on a level test (ADR 0071 §6) - the ladder's length and the sheet's level.
    const { distinct, levelsOn, ladderLength, sheetLevel } = useSetting((settings) => ({
        distinct: settings.ClassSetDistinctProblems,
        levelsOn: levelApplies(settings),
        ladderLength: ladderForSettings(settings)?.levels.length ?? 0,
        sheetLevel: levelOf(settings)?.id ?? 0,
    }));
    const { entries, names, overflowed } = parseRoster(roster);
    // A level test lets each line carry its own level; say how, and say when a number is not one.
    const withLevels = levelsOn;
    const unknownLevels = levelsOn ? countOutOfRange(entries, ladderLength) : 0;
    const t = useTranslation();

    return (
        <details className="classRoster">
            <summary>{t('settings.printClassSet')}</summary>
            <p className="classRosterHint">
                {t('settings.classRosterHint')}
            </p>
            {withLevels && (
                <p className="classRosterHint">{t('settings.classRosterLevelHint')}</p>
            )}
            <textarea
                className="classRosterInput"
                rows={6}
                value={roster}
                onChange={(event) => onRosterChange(event.target.value)}
                aria-label={t('settings.classRosterAria')}
                placeholder={t('settings.classRosterPlaceholder')}
            />
            <p className="classRosterCount">
                {names.length} {names.length === 1 ? t('settings.name') : t('settings.names')}
                {overflowed && format(t('settings.rosterOverflow'), { max: MAX_ROSTER })}
                {unknownLevels > 0 && format(t('settings.rosterLevelOutOfRange'), {
                    count: unknownLevels,
                    level: sheetLevel,
                })}
            </p>
            <div className="answerPagesContainer">
                <label htmlFor="classSetDistinct">{t('settings.differentProblemsPerStudent')}</label>
                <ToggleButton onLabel={t('settings.toggleYes')} offLabel={t('settings.toggleNo')}
                    inputId="classSetDistinct"
                    className="answerPagesButton"
                    checked={distinct}
                    onChange={(e) =>
                        setSettings({ ...getSettings(), ClassSetDistinctProblems: e.value })
                    }
                />
            </div>
        </details>
    );
}

export default ClassRoster;
